import { CircleCheck, CircleMinus, CircleX, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { COMPANY_STATUS_CONFIG } from "~/components/company/company-header";
import { VAT_VERIFICATION_PRESENTATION } from "~/lib/vat-verification";
import type { CompanyProfile } from "~/types/company";
import { OverviewCard } from "./card";

type RiskTone = "ok" | "risk" | "neutral";

function RiskRow({ tone, to, children }: { tone: RiskTone; to?: string; children: ReactNode }) {
	const icon =
		tone === "ok" ? (
			<CircleCheck className="h-4 w-4 shrink-0 text-green-600" />
		) : tone === "risk" ? (
			<CircleX className="h-4 w-4 shrink-0 text-red-600" />
		) : (
			<CircleMinus className="h-4 w-4 shrink-0 text-gray-400" />
		);

	const textClass =
		tone === "ok"
			? "text-green-700"
			: tone === "risk"
				? "text-red-700 font-medium"
				: "text-muted-foreground";

	const content = <span className={`text-sm ${textClass}`}>{children}</span>;

	return (
		<li className="flex items-center gap-2.5">
			{icon}
			{to ? (
				<Link to={to} prefetch="intent" className="hover:underline">
					{content}
				</Link>
			) : (
				content
			)}
		</li>
	);
}

/** ① Rizika a důvěryhodnost — green/red checklist of trust signals. */
export function RiskCard({ company }: { company: CompanyProfile }) {
	const vat = company.vatRegistration;
	const statusLabel = COMPANY_STATUS_CONFIG[company.status]?.label ?? company.status;
	const hasBankAccounts = (vat?.bankAccounts?.length ?? 0) > 0;
	const isRegistered = company.viewerTier !== "anon";
	const vatStatusIsCurrent = vat?.verificationStatus === "VERIFIED";
	const vatWasChecked = Boolean(company.dataFreshness?.vat);

	return (
		<OverviewCard icon={ShieldCheck} title="Rizika a důvěryhodnost" hue="amber">
			<ul className="flex flex-col gap-2.5">
				{!isRegistered ? (
					<RiskRow tone="neutral">Insolvenční údaje po přihlášení</RiskRow>
				) : company.adverseDataRestricted ? (
					<RiskRow tone="neutral">Rizikové údaje se prověřují</RiskRow>
				) : company.hasActiveInsolvency ? (
					<RiskRow tone="risk" to="insolvence">
						Ověřený aktivní záznam v ISIR
					</RiskRow>
				) : company.hasHistoricalInsolvency ? (
					<RiskRow tone="neutral" to="insolvence">
						Historický záznam v ISIR
					</RiskRow>
				) : company.hasUnverifiedInsolvencyData ? (
					<RiskRow tone="neutral">Insolvenční údaje se ověřují</RiskRow>
				) : (
					<RiskRow tone="neutral">Nemáme aktuální ověřený záznam v ISIR</RiskRow>
				)}

				{!isRegistered ? (
					<RiskRow tone="neutral">Spolehlivost DPH po přihlášení</RiskRow>
				) : vat?.isUnreliable && vatStatusIsCurrent ? (
					<RiskRow tone="risk" to="dph">
						Nespolehlivý plátce DPH
					</RiskRow>
				) : vat?.isVatPayer && vatStatusIsCurrent ? (
					<RiskRow tone="ok">V ADIS není označen jako nespolehlivý</RiskRow>
				) : vat?.isVatPayer ? (
					<RiskRow tone="neutral">
						{VAT_VERIFICATION_PRESENTATION[vat.verificationStatus].label}
					</RiskRow>
				) : vatWasChecked ? (
					<RiskRow tone="ok">Ověřeno – není plátce DPH</RiskRow>
				) : (
					<RiskRow tone="neutral">Údaj o registraci DPH není dostupný</RiskRow>
				)}

				{company.status === "ACTIVE" ? (
					<RiskRow tone="ok">Aktivní firma</RiskRow>
				) : (
					<RiskRow tone="risk">{statusLabel}</RiskRow>
				)}

				{hasBankAccounts && (
					<RiskRow tone="ok" to="dph">
						Zveřejněné bankovní účty (§ 109)
					</RiskRow>
				)}
			</ul>
		</OverviewCard>
	);
}
